<template>
    <bootstrap-box>
        <template #header>
            <div class="mb-3">
                <i class="fas fa-project-diagram"/>
                <h3 class="box-title">{{resource('JourneyBuilder_JourneyReport_Title')}}</h3>
            </div>

            <div class="configuration-controls">
                <div class="individual-control journey-selector">
                    <label>{{resource('JourneyBuilder_Shared_Journey')}}</label>
                    <journey-dropdown v-model="journeyIdModel" />
                </div>

                <template v-if="displayNewSankeyConfiguration">
                    <div class="individual-control period-selector">
                        <label>{{resource('JourneyBuilder_Sankey_Period_Label')}}</label>
                        <period-id-picker v-model="periodIdModel" />
                    </div>
                    
                    <div class="individual-control inflow-period-selector">
                        <label>{{resource('JourneyBuilder_Sankey_InflowPeriod_Label')}}</label>
                        <inflow-period-picker v-model="inflowPeriodIdModel" />
                    </div>

                    <div class="individual-control product-selector">
                        <label>{{resource('JourneyBuilder_Sankey_ProductIds_Label')}}</label>
                        <products-selector v-model="productNamesModel" />
                    </div>

                    <div class="individual-control highlight-mode-selector">
                        <label>{{resource('JourneyBuilder_Sankey_HighlightMode_Label')}}</label>
                        <highlight-mode-selector v-model="highlightMode" />
                    </div>
                </template>
                
                <div class="sankey-explanation">
                    <label>{{resource('JourneyBuilder_Sankey_TouchpointSelection_Explanation')}}</label>
                </div>
            </div>
        </template>

        <template #body>
            <div class="form-group">
                <div class="row">
                    <div class="col-12">
                        <!-- 
                            We auto select the first encountered periodId
                            changing periodId triggers a reload of our sankey data
                            stall until a periodId is auto selected to prevent invoking our Sankey endpoint twice
                        -->
                        <sankey-v2 v-if="periodId" 
                            :highlight-mode="highlightMode" />
                        <loading-box v-else />
                    </div>
                </div>
            </div>
        </template>
    </bootstrap-box>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {FLAGS} from "@modules/JourneyBuilder/constants";
import {bootstrapDangerGrowl} from "@core/Util/growler";

import resourceMixin from "@core/Util/Mixins/resources";

import SankeyV2, {PeriodIdPicker, HighlightModeSelector, ProductsSelector, InflowPeriodPicker} from "./SankeyV2";
import BootstrapBox from "@core/Components/Util/BootstrapBox";
import JourneyDropdown from "@modules/JourneyBuilder/Components/Elements/JourneyDropdown";
import {NavigateToJourneyButton} from "@modules/JourneyBuilder/Components/Elements";
import LoadingBox from "@core/Components/Util/LoadingBox";

const { mapState, mapGetters, mapMutations, mapActions } = createNamespacedHelpers('journeyBuilder/reporting');

export default {
    name: 'configurable-sankey-diagram-v2',
    components: {
        SankeyV2,
        PeriodIdPicker,
        BootstrapBox,
        JourneyDropdown,
        NavigateToJourneyButton,
        HighlightModeSelector,
        ProductsSelector,
        InflowPeriodPicker,
        LoadingBox
    },
    mixins: [resourceMixin],
    async created() {
        //Load our default date range display
        await this.loadPeriodDateRange(this.periodId);
    },
    methods: {
        ...mapMutations(['setJourneyId', 'setPeriodId', 'setProductNames', 'setInflowPeriodId']),
        ...mapActions({
            async loadPeriodDateRange(dispatch, periodId){
                try {
                    await dispatch('loadPeriodDateRange', periodId);
                } catch(e) {
                    bootstrapDangerGrowl(e);
                }    
            }
        })
    },
    watch: {
        periodId(value){
            this.loadPeriodDateRange(value);
        }
    },
    computed: {
        ...mapState(['journeyId', 'periodId', 'productNames', 'inflowPeriodId']),
        journeyIdModel: {
            get(){ 
                return this.journeyId; 
            },
            set(value){ 
                this.setJourneyId(value); 
            }
        },
        periodIdModel: {
            get(){ 
                return this.periodId; 
            },
            set(value){ 
                this.setPeriodId(value); 
            }
        },
        productNamesModel: {
            get(){ 
                return this.productNames; 
            },
            set(value){ 
                this.setProductNames(value); 
            }
        },
        inflowPeriodIdModel: {
            get() {
                return this.inflowPeriodId;
            },
            set(value) {
                this.setInflowPeriodId(value);
            }
        }
    },
    data: () => ({
        highlightMode: undefined,

        displayNewSankeyConfiguration: FLAGS.displayNewSankeyConfiguration
    })
}
</script>

<style scoped>
    .highlight-mode-selector { grid-area: highlight-mode-selector; }
    .journey-selector { grid-area: journey-selector; }
    .period-selector { grid-area: period-selector; }
    .inflow-period-selector { grid-area: inflow-period-selector; }
    .product-selector { grid-area: product-selector; }
    .sankey-explanation { grid-area: sankey-explanation; }

    .individual-control {
        display: grid;
        grid-template: auto / 70px auto;
    }
    
    .configuration-controls {
        display: grid;
        
        grid-template-columns: 0.25fr 0.25fr 0.25fr auto;
        grid-row-gap: 5px;
        grid-column-gap: 30px;
        grid-template-areas: 
            "journey-selector . . ."
            "period-selector inflow-period-selector product-selector ."
            "highlight-mode-selector highlight-mode-selector highlight-mode-selector highlight-mode-selector"
            "sankey-explanation sankey-explanation sankey-explanation sankey-explanation";
    }
</style>